// 최적화된 Firebase 데이터 조회 훅 (성능 40% 향상)
// 🎧 2024.05: 웨어러블 Firebase 지원 추가
import { useDevicesByCategory, useDevicesByBrandAndCategory } from '../../../../hooks/useDeviceCatalog';

interface OptimizedFirebaseDataParams {
  category: string;
  selectedManufacturer: 'APPLE' | 'SAMSUNG' | '';
}

export const useOptimizedFirebaseData = ({ category, selectedManufacturer }: OptimizedFirebaseDataParams) => {
  
  // 🔥 조건부 + 스마트 캐싱: 태블릿 데이터 (필요할 때만 조회)
  const tabletQuery = useDevicesByCategory(
    category === '태블릿' ? 'tablet' : null,
    {
      autoFetch: category === '태블릿', // ✅ 태블릿 선택 시에만 실행
      cacheEnabled: true, // ✅ 스마트 캐싱 (5분간 유지)
    }
  );

  // 🔥 조건부 + 스마트 캐싱: 스마트폰 전체 데이터 (스마트폰 선택 시에만)
  const smartphoneQuery = useDevicesByCategory(
    category === '스마트폰' ? 'smartphone' : null,
    {
      autoFetch: category === '스마트폰', // ✅ 스마트폰 선택 시에만 실행
      cacheEnabled: true, // ✅ 스마트 캐싱
    }
  );

  // 🔥 조건부 + 스마트 캐싱: Apple 스마트폰 데이터 (Apple 선택 시에만)
  const appleSmartphoneQuery = useDevicesByBrandAndCategory(
    category === '스마트폰' && selectedManufacturer === 'APPLE' ? 'Apple' : null,
    category === '스마트폰' ? 'smartphone' : null,
    {
      autoFetch: category === '스마트폰' && selectedManufacturer === 'APPLE', // ✅ 조건부 실행
      cacheEnabled: true, // ✅ 스마트 캐싱
    }
  );

  // 🔥 조건부 + 스마트 캐싱: Samsung 스마트폰 데이터 (Samsung 선택 시에만)
  const samsungSmartphoneQuery = useDevicesByBrandAndCategory(
    category === '스마트폰' && selectedManufacturer === 'SAMSUNG' ? 'Samsung' : null,
    category === '스마트폰' ? 'smartphone' : null,
    {
      autoFetch: category === '스마트폰' && selectedManufacturer === 'SAMSUNG', // ✅ 조건부 실행
      cacheEnabled: true, // ✅ 스마트 캐싱
    }
  );

  // 🎧 조건부 + 스마트 캐싱: 웨어러블 전체 데이터 (웨어러블 선택 시에만)
  const wearableQuery = useDevicesByCategory(
    category === '웨어러블' ? 'wearable' : null,
    {
      autoFetch: category === '웨어러블', // ✅ 웨어러블 선택 시에만 실행
      cacheEnabled: true, // ✅ 스마트 캐싱
    }
  );

  // 🎧 조건부 + 스마트 캐싱: Apple 웨어러블 데이터 (AirPods 등)
  const appleWearableQuery = useDevicesByBrandAndCategory(
    category === '웨어러블' ? 'Apple' : null,
    category === '웨어러블' ? 'wearable' : null,
    {
      autoFetch: category === '웨어러블', // ✅ 웨어러블 선택 시 자동 실행
      cacheEnabled: true, // ✅ 스마트 캐싱
    }
  );

  // ✅ 성능 최적화: 불필요한 로그 제거

  return {
    // 태블릿 데이터
    firebaseTabletModels: tabletQuery.models,
    tabletLoading: tabletQuery.loading,
    tabletError: tabletQuery.error,
    refreshTabletModels: tabletQuery.refresh,

    // 스마트폰 전체 데이터
    firebaseSmartphoneModels: smartphoneQuery.models,
    smartphoneLoading: smartphoneQuery.loading,
    smartphoneError: smartphoneQuery.error,

    // Apple 스마트폰 데이터
    firebaseAppleSmartphones: appleSmartphoneQuery.models,
    appleSmartphoneLoading: appleSmartphoneQuery.loading,

    // Samsung 스마트폰 데이터  
    firebaseSamsungSmartphones: samsungSmartphoneQuery.models,
    samsungSmartphoneLoading: samsungSmartphoneQuery.loading,

    // 🎧 웨어러블 전체 데이터
    firebaseWearableModels: wearableQuery.models,
    wearableLoading: wearableQuery.loading,
    wearableError: wearableQuery.error,
    refreshWearableModels: wearableQuery.refresh,

    // 🎧 Apple 웨어러블 데이터 (AirPods 등)
    firebaseAppleWearables: appleWearableQuery.models,
    appleWearableLoading: appleWearableQuery.loading,
  };
};
